import { useState, useEffect, useRef, useMemo } from 'react';
import { useReadContract, useReadContracts } from 'wagmi';
import { COHORT_ABI } from '../contract/abi';
import { deriveParticipantId } from '../utils/crypto';
import { signRecordSession } from '../utils/signing';
import { relayRecordSession, isRelayerConfigured } from '../services/relayer';

interface RecordSessionProps {
  cohortId: `0x${string}`;
  cohortTitle: string;
  onBack: () => void;
  onSessionRecorded?: () => void;
}

export function RecordSession({ cohortId, cohortTitle, onBack, onSessionRecorded }: RecordSessionProps) {
  const [participants, setParticipants] = useState<string[]>([]);
  const [newParticipant, setNewParticipant] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState<Set<string>>(new Set());
  const [points, setPoints] = useState<Map<string, number>>(new Map());
  const [importance, setImportance] = useState(2);
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);
  const prevSelectedCount = useRef(0);

  const contractAddress = import.meta.env.VITE_CONTRACT_ADDRESS as `0x${string}`;

  // Get participant IDs from chain
  const { data: participantIds } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'getParticipantIds',
    args: [cohortId],
  });

  // Build contract read configs for participant names
  const participantNameConfigs = useMemo(() => {
    if (!participantIds || participantIds.length === 0) return [];
    return participantIds.map((id) => ({
      address: contractAddress,
      abi: COHORT_ABI,
      functionName: 'names' as const,
      args: [id] as const,
    }));
  }, [participantIds, contractAddress]);

  // Fetch participant names from chain
  const { data: participantNameResults } = useReadContracts({
    contracts: participantNameConfigs,
  });

  // Load participant names from chain when available
  useEffect(() => {
    if (!participantNameResults) return;
    const names = participantNameResults
      .map((result) => result?.result as string | undefined)
      .filter((name): name is string => !!name && name.length > 0);
    if (names.length > 0) {
      setParticipants(names);
    }
  }, [participantNameResults]);

  // Get current session count for nonce - must be fresh to avoid InvalidSignature
  const { refetch: refetchSessionCount } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'sessionCount',
    args: [cohortId],
    query: {
      refetchOnMount: 'always',
      staleTime: 0,
    },
  });

  // For now, just use manual participant entry
  // In a full implementation, we'd load existing participants from the chain and look up names from the registry

  const handleAddParticipant = () => {
    const trimmed = newParticipant.trim();
    if (trimmed && !participants.includes(trimmed)) {
      setParticipants([...participants, trimmed]);
      setSelectedParticipants(new Set([...selectedParticipants, trimmed]));
      setNewParticipant('');
    }
  };

  // Auto-distribute evenly when participant count changes
  useEffect(() => {
    if (selectedParticipants.size !== prevSelectedCount.current) {
      prevSelectedCount.current = selectedParticipants.size;
      if (selectedParticipants.size > 0) {
        const selected = Array.from(selectedParticipants);
        const perPerson = Math.floor(100 / selected.length);
        const remainder = 100 - perPerson * selected.length;
        const newPoints = new Map<string, number>();
        selected.forEach((name, index) => {
          newPoints.set(name, perPerson + (index < remainder ? 1 : 0));
        });
        setPoints(newPoints);
      }
    }
  }, [selectedParticipants]);

  const toggleParticipant = (name: string) => {
    const newSelected = new Set(selectedParticipants);
    if (newSelected.has(name)) {
      newSelected.delete(name);
    } else {
      newSelected.add(name);
    }
    setSelectedParticipants(newSelected);
  };

  const updatePoints = (name: string, newValue: number) => {
    const currentValue = points.get(name) || 0;
    const currentTotal = getTotalPoints();
    const othersTotal = currentTotal - currentValue;

    // Hard cap: can't exceed what's available
    const maxAllowed = 100 - othersTotal;
    const clampedValue = Math.min(Math.max(0, newValue), maxAllowed);

    const newPoints = new Map(points);
    newPoints.set(name, clampedValue);
    setPoints(newPoints);
  };

  // Get the max value this slider can go to (for showing red zone)
  const getMaxAllowed = (name: string) => {
    const currentValue = points.get(name) || 0;
    const currentTotal = getTotalPoints();
    const othersTotal = currentTotal - currentValue;
    return 100 - othersTotal;
  };

  const getTotalPoints = () => {
    let sum = 0;
    selectedParticipants.forEach(name => {
      sum += points.get(name) || 0;
    });
    return sum;
  };

  const getRemainingPoints = () => {
    return 100 - getTotalPoints();
  };

  const distributeEvenly = () => {
    const selected = Array.from(selectedParticipants);
    if (selected.length === 0) return;

    const perPerson = Math.floor(100 / selected.length);
    const remainder = 100 - perPerson * selected.length;

    const newPoints = new Map<string, number>();
    selected.forEach((name, index) => {
      newPoints.set(name, perPerson + (index < remainder ? 1 : 0));
    });
    setPoints(newPoints);
  };

  const handleSubmit = async () => {
    setError(null);
    setIsPending(true);

    // Validation
    if (selectedParticipants.size === 0) {
      setError('Please select at least one participant');
      setIsPending(false);
      return;
    }

    const totalPoints = getTotalPoints();
    if (totalPoints !== 100) {
      setError(`Points must sum to 100 (current: ${totalPoints})`);
      setIsPending(false);
      return;
    }

    try {
      // Trim whitespace from all names before submitting to chain
      const participantNames = Array.from(selectedParticipants).map(name => name.trim());
      const trimmedCohortTitle = cohortTitle.trim();
      const participantIds = participantNames.map(name => deriveParticipantId(name));
      const pointsArray = Array.from(selectedParticipants).map(name => BigInt(points.get(name) || 0));

      // Refetch session count to ensure we have the latest nonce
      const { data: freshSessionCount } = await refetchSessionCount();
      const nonce = freshSessionCount ?? 0n;

      // Sign the message using passkey
      const sig = await signRecordSession(
        cohortId,
        nonce,
        importance,
        participantIds,
        pointsArray
      );

      if (!isRelayerConfigured()) {
        setError('Relayer not configured. Set VITE_RELAYER_URL environment variable.');
        setIsPending(false);
        return;
      }

      // Submit via relayer with plaintext names (trimmed)
      const result = await relayRecordSession({
        cohortTitle: trimmedCohortTitle,
        participantNames,
        importance,
        points: pointsArray,
        v: sig.v,
        r: sig.r,
        s: sig.s,
      });

      if (!result.success) {
        setError(result.error || 'Transaction failed');
        setIsPending(false);
        return;
      }

      setTxHash(result.txHash || null);
      setIsSuccess(true);
      // Names are automatically registered on-chain by the relayer
      // Trigger refetch in parent component
      onSessionRecorded?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record session');
    } finally {
      setIsPending(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="container">
        <div className="card">
          <div className="message message-success">
            Session recorded successfully!
            {txHash && (
              <p style={{ fontSize: '0.875rem', marginTop: 'var(--space-xs)' }}>
                Tx: <a
                  href={`https://optimistic.etherscan.io/tx/${txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit' }}
                >
                  {txHash.slice(0, 10)}...{txHash.slice(-8)}
                </a>
              </p>
            )}
          </div>
          <button onClick={onBack} className="btn-primary">
            Back to Cohort
          </button>
        </div>
      </div>
    );
  }

  const totalPoints = getTotalPoints();
  const remaining = getRemainingPoints();
  const isValid = totalPoints === 100 && selectedParticipants.size > 0;

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <h1>Record Session</h1>
          <button onClick={onBack} className="btn-secondary">
            Back
          </button>
        </div>

        {error && (
          <div className="message message-error">
            {error}
          </div>
        )}

        <div className="form-group">
          <label>Importance Level (0-5)</label>
          <div className="flex gap-md" style={{ flexWrap: 'wrap' }}>
            {[0, 1, 2, 3, 4, 5].map((level) => (
              <label key={level} className="checkbox-label" style={{ flex: '1 1 auto', minWidth: '80px' }}>
                <input
                  type="radio"
                  name="importance"
                  value={level}
                  checked={importance === level}
                  onChange={() => setImportance(level)}
                />
                <span>
                  {level}
                  <span className="importance-indicator">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <span
                        key={i}
                        className={`importance-dot ${i <= level ? 'active' : ''}`}
                      />
                    ))}
                  </span>
                </span>
              </label>
            ))}
          </div>
          <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 'var(--space-xs)' }}>
            {importance === 0 && '0: Little stuff, quick chat or minor task (0.25x)'}
            {importance === 1 && '1: Quick sync, routine (30-60 min standup)'}
            {importance === 2 && '2: Working session, got stuff done (couple hours)'}
            {importance === 3 && '3: Half-day deep dive, real progress'}
            {importance === 4 && '4: Full day intensive, major milestone'}
            {importance === 5 && '5: Multi-day effort, project-defining'}
          </p>
        </div>

        <div className="form-group">
          <label>Add Participants</label>
          <div style={{ display: 'flex', gap: 'var(--space-sm)', minWidth: 0 }}>
            <input
              type="text"
              value={newParticipant}
              onChange={(e) => setNewParticipant(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddParticipant()}
              placeholder="Enter participant name"
              style={{ flex: 1, minWidth: 0 }}
            />
            <button
              onClick={handleAddParticipant}
              className="btn-secondary"
              disabled={!newParticipant.trim() || participants.includes(newParticipant.trim())}
              style={{ flexShrink: 0 }}
            >
              Add
            </button>
          </div>
        </div>

        {participants.length > 0 && (
          <div className="form-group">
            <div className="flex justify-between items-center mb-sm">
              <label>Select Participants</label>
              <button
                onClick={distributeEvenly}
                className="btn-secondary btn-small"
                disabled={selectedParticipants.size === 0}
              >
                Distribute Evenly
              </button>
            </div>
            <div className="participant-checkboxes">
              {participants.map((name) => (
                <label key={name} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedParticipants.has(name)}
                    onChange={() => toggleParticipant(name)}
                  />
                  {name}
                </label>
              ))}
            </div>
          </div>
        )}

        {selectedParticipants.size > 0 && (
          <div className="form-group">
            <label>Point Allocation (must sum to 100)</label>
            <div
              className={`message ${
                remaining === 0
                  ? 'message-success'
                  : remaining > 0
                  ? 'message-info'
                  : 'message-error'
              }`}
            >
              Total: {totalPoints} / 100 | Remaining: {remaining}
            </div>

            <div className="slider-container" style={{ marginTop: 'var(--space-md)' }}>
              {Array.from(selectedParticipants).map((name) => {
                const currentPoints = points.get(name) || 0;
                const maxAllowed = getMaxAllowed(name);
                const isAtCap = maxAllowed < 100;

                return (
                  <div key={name} className="slider-row" style={{ marginBottom: 'var(--space-md)' }}>
                    <div className="slider-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-xs)' }}>
                      <label style={{ fontWeight: 500 }}>{name}</label>
                      <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{currentPoints} pts</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={currentPoints}
                          onChange={(e) => updatePoints(name, parseInt(e.target.value) || 0)}
                          style={{
                            width: '100%',
                            cursor: 'pointer',
                            background: isAtCap
                              ? `linear-gradient(to right, var(--charcoal-light) 0%, var(--charcoal-light) ${maxAllowed}%, var(--error) ${maxAllowed}%, var(--error) 100%)`
                              : undefined
                          }}
                        />
                      </div>
                      <input
                        type="number"
                        min="0"
                        max={maxAllowed}
                        value={currentPoints}
                        onChange={(e) => updatePoints(name, parseInt(e.target.value) || 0)}
                        style={{ width: '70px', textAlign: 'center' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex gap-md mt-lg">
          <button
            onClick={handleSubmit}
            disabled={!isValid || isPending}
            className="btn-primary"
          >
            {isPending ? (
              <>
                <span className="spinner" />
                Recording...
              </>
            ) : (
              'Record Session'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
